import { TankPlanFilter } from 'src/enums/stowage/tankPlanFilter';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getPreviousDistinctCargoContentsInTank } from './manifest/cargoManifestUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { ProductAllowanceEnum } from 'src/types/BusinessModel/Stowage/Product/ProductAllowanceEnum';
import { CoatingTypeEnum } from 'src/enums/stowage/coatingTypeEnum';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';

/* Restrictions beyond the Immediate Previous Cargo:
- Leaded products shall not be carried as the three previous cargoes.
- Ethylene Dichloride and Styrene Monomer (both of which are on the FOSFA Banned List) shall not be carried as the three previous cargoes in organic coated tanks, or as the last cargo in stainless steel and inorganic coated tanks.
*/
const ibcProductNamesExtraRestrictions = ['Ethylene dichloride', 'Styrene monomer'];
const numberOfLastCargoesForEpoxyTanksFosfaBan = 3;

const checkFosfaCondition = (cargoes: CargoContent[], index: number, fosfaCondition: (fosfaAllowance: ProductAllowanceEnum) => boolean): boolean => {
    const categoryName = cargoes[index]?.cargo.product.category.name;
    const isCategoryRestricted = ibcProductNamesExtraRestrictions.includes(categoryName);
    const isNumberConditionMet = numberOfLastCargoesForEpoxyTanksFosfaBan < index + 1;
    const fosfaAllowance = cargoes[index]?.cargo.product.fosfaAllowance;
    const isConditionMet = fosfaCondition(fosfaAllowance);

    if (index === 0) {
        return isConditionMet;
    }

    if (!isCategoryRestricted && index > 0) {
        return true;
    }

    return isNumberConditionMet && isConditionMet;
};

const isFosfaAcceptable = (cargoes: CargoContent[], index: number): boolean => {
    return checkFosfaCondition(cargoes, index, (fosfaAllowance) => fosfaAllowance === ProductAllowanceEnum.Acceptable);
};

const isFosfaNotBanned = (cargoes: CargoContent[], index: number): boolean => {
    return checkFosfaCondition(cargoes, index, (fosfaAllowance) => fosfaAllowance !== ProductAllowanceEnum.Banned);
};

const FOSFA_MIN_FILL_RATIO = 0.6;

export const getCargoFosfaCellStatus = (cargo: CargoContent, fillRatio: number | null): 'acceptable' | 'banned' | 'none' => {
    const fosfaAllowance = cargo.cargo.product.fosfaAllowance;

    if (fosfaAllowance === ProductAllowanceEnum.Banned) {
        return 'banned';
    }

    if (fosfaAllowance === ProductAllowanceEnum.Acceptable && fillRatio !== null && fillRatio > FOSFA_MIN_FILL_RATIO) {
        return 'acceptable';
    }

    return 'none';
};

export const isTankAllowedWithSelectedTankPlanFilter = (
    stowagePlan: StowagePlan,
    tank: Tank,
    berthCall: RotationBerthCall,
    selectedTankPlanFilters: TankPlanFilter[],
    fillRatio: number
): boolean => {
    if (selectedTankPlanFilters.length === 0) {
        return true;
    }
    const previousCargoes = getPreviousDistinctCargoContentsInTank(stowagePlan, tank, berthCall, 3);

    if (previousCargoes.length === 0) {
        return true;
    }

    let isTankAllowed = true;

    for (const filter of selectedTankPlanFilters) {
        switch (filter) {
            case TankPlanFilter.EuAccepted:
                isTankAllowed = isTankAllowed && previousCargoes[0].cargo.product.euAllowance === ProductAllowanceEnum.Acceptable;
                break;
            case TankPlanFilter.Niop1Accepted:
                isTankAllowed = isTankAllowed && previousCargoes[0].cargo.product.niop1Allowance === ProductAllowanceEnum.Acceptable;
                break;
            case TankPlanFilter.Niop2Accepted:
                isTankAllowed = isTankAllowed && previousCargoes[0].cargo.product.niop2Allowance === ProductAllowanceEnum.Acceptable;
                break;
            case TankPlanFilter.FosfaAccepted:
                isTankAllowed = isTankAllowed && isFosfaAcceptable(previousCargoes, 0);

                if (tank.coatingType === CoatingTypeEnum.Epoxy) {
                    isTankAllowed = isTankAllowed && isFosfaAcceptable(previousCargoes, 1);
                    isTankAllowed = isTankAllowed && isFosfaAcceptable(previousCargoes, 2);
                }

                isTankAllowed = isTankAllowed && fillRatio > FOSFA_MIN_FILL_RATIO;
                break;
            case TankPlanFilter.NotFosfaBanned:
                isTankAllowed = isTankAllowed && isFosfaNotBanned(previousCargoes, 0);

                if (tank.coatingType === CoatingTypeEnum.Epoxy) {
                    isTankAllowed = isTankAllowed && isFosfaNotBanned(previousCargoes, 1);
                    isTankAllowed = isTankAllowed && isFosfaNotBanned(previousCargoes, 2);
                }
                break;
        }

        if (!isTankAllowed) {
            break;
        }
    }

    return isTankAllowed;
};
